// src/components/NotificationBell.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot, updateDoc, doc, writeBatch, getDocs, where, setDoc, serverTimestamp } from 'firebase/firestore';
import { FaBell, FaTimes, FaArrowRight, FaCheckCircle, FaExclamationTriangle, FaUserPlus, FaDollarSign, FaFileAlt, FaUsers, FaSwimmingPool, FaShieldAlt, FaCogs } from 'react-icons/fa';
import { useNotificationSound } from '../hooks/useNotificationSound';
import { useRestriccionMorosidad } from '../hooks/useRestriccionMorosidad';
import './NotificationBell.css';

export const limpiarTextoNotificacion = (texto) => {
  if (!texto || typeof texto !== 'string') return texto || '';
  return texto
    .replace(/ðŸ“£/g, '📢')
    .replace(/âš\s*ï¸/g, '⚠️')
    .replace(/âš/g, '⚠️')
    .replace(/ðŸ—“ï¸/g, '📅')
    .replace(/ðŸ¤\s*/g, '🤝')
    .replace(/ðŸ“„/g, '📄')
    .replace(/â\s*³/g, '⏳')
    .replace(/Ã¡/g, 'á')
    .replace(/Ã©/g, 'é')
    .replace(/Ã­/g, 'í')
    .replace(/Ã³/g, 'ó')
    .replace(/Ãº/g, 'ú')
    .replace(/Ã±/g, 'ñ')
    .replace(/Ã/g, 'Á');
};

const NotificationBell = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('todas');
  const { playNotif } = useNotificationSound();
  const { userDebt } = useRestriccionMorosidad();
  const prevUnreadCount = useRef(0);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  // Reiniciar scroll a la notificación más reciente (arriba) al cambiar de sección o abrir panel
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [selectedTab, showPanel]);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const notifRef = collection(db, 'usuarios', currentUser.uid, 'notificaciones');
    const q = query(notifRef, orderBy('fecha', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setNotifications(notifs);
      setLoading(false);
    }, (error) => {
      console.error('🔔 NotificationBell: Error escuchando notificaciones en tiempo real:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Efecto para reproducir sonido si hay nuevas notificaciones no leídas
  useEffect(() => {
    const sonidosHabilitados = userData?.preferencias?.sonidosHabilitados !== false;

    if (unreadCount > prevUnreadCount.current && unreadCount > 0 && sonidosHabilitados) {
      console.log('🔔 Intentando reproducir sonido de notificación. Conteo:', unreadCount);
      playNotif();
    }
    prevUnreadCount.current = unreadCount;
  }, [unreadCount, playNotif, userData?.preferencias?.sonidosHabilitados]);

  // Cerrar panel al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showPanel &&
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPanel]);

  // Función para determinar la sección de una notificación
  const getNotificationSection = (tipo) => {
    const sectionMap = {
      // Comunidad
      'votacion': 'comunidad',
      'aviso': 'comunidad',
      'aviso_general': 'comunidad',
      'propuesta': 'comunidad',
      'incidencia': 'comunidad',
      'solicitud_mudanza': 'comunidad',
      'solicitud_construccion': 'comunidad',
      'solicitud_remodelacion': 'comunidad',
      'solicitud': 'comunidad',
      'documento': 'comunidad',
      'permiso': 'comunidad',
      'evento_cita': 'comunidad',
      'chat_mensaje': 'comunidad',
      'reglamento_nuevo': 'comunidad',
      // Amenidades
      'reservacion': 'amenidades',
      'reservacion_confirmada': 'amenidades',
      'reservacion_cancelada': 'amenidades',
      'mantenimiento': 'amenidades',
      'horario': 'amenidades',
      'reserva_amenidad': 'amenidades',
      'reserva_cancelada': 'amenidades',
      // Vigilancia
      'visita_frecuente': 'vigilancia',
      'visita_frecuente_actualizada': 'vigilancia',
      'visita_frecuente_eliminada': 'vigilancia',
      'visita_frecuente_nueva': 'vigilancia',
      'acceso_autorizado': 'vigilancia',
      'acceso_qr_validado': 'vigilancia',
      'qr_generado_vigilancia': 'vigilancia',
      'qr_visita_entrada': 'vigilancia',
      'qr_visita_salida': 'vigilancia',
      'paquete': 'vigilancia',
      'paquete_nuevo': 'vigilancia',
      'paquete_entregado': 'vigilancia',
      'entrada_manual': 'vigilancia',
      'visita': 'vigilancia',
      'visita_registrada': 'vigilancia',
      // Finanzas
      'comprobante_pago': 'finanzas',
      'comprobante': 'finanzas',
      'pago_registrado': 'finanzas',
      'advertencia_corte': 'finanzas',
      'corte_aplicado': 'finanzas',
      'sanciones_aplicadas': 'finanzas',
      'cambio_configuracion_financiera': 'finanzas',
      'multa_admin': 'finanzas',
      'multa_leida_confirmacion': 'finanzas',
      // Administración
      'correo_personalizado': 'administracion',
      'convenio_activado': 'administracion',
      'convenio_confirmado': 'administracion',
      // Configuraciones
      'configuracion': 'configuraciones',
      'cambio_configuracion': 'configuraciones',
      'horario_mudanza': 'configuraciones',
      'horario_amenidad': 'configuraciones',
      'reglamento_circulacion': 'configuraciones',
      'configuracion_general': 'configuraciones',
    };
    return sectionMap[tipo] || 'comunidad'; // Por defecto comunidad
  };

  const userRole = String(userData?.rol || '').toLowerCase().trim();
  const isAdmin = ['superadmin', 'admin', 'manager', 'plataforma', 'admin_fracc', 'platformadmin'].includes(userRole);
  const isResidentMoroso = Boolean(userDebt?.moroso);

  // Filtrar notificaciones:
  // 1. Blindaje de privacidad para morosos: Si el residente está al corriente, omitir notificaciones para morosos.
  // 2. Filtro del mes actual: guardar en Firestore las de meses anteriores sin mostrarlas en la bandeja activa.
  const notificacionesMesActual = useMemo(() => {
    const ahora = new Date();
    const mesActual = ahora.getMonth();
    const anioActual = ahora.getFullYear();

    return notifications.filter((notif) => {
      // Blindaje de morosidad
      const esExclusivoMorosos = Boolean(
        notif.enviarSoloMorosos ||
        notif.soloMorosos ||
        notif.tipoDestinatarios === 'morosos' ||
        notif.tipo === 'recordatorio_corte' ||
        notif.tipo === 'recordatorio_sancion' ||
        notif.tipo === 'sanciones_aplicadas' ||
        notif.tipo === 'advertencia_corte'
      );

      if (!isAdmin && !isResidentMoroso && esExclusivoMorosos) {
        return false;
      }

      // Blindaje de domicilios específicos
      const esPersonalizado = notif.tipoDestinatarios === 'personalizado' || (Array.isArray(notif.casasHabilitadas) && notif.casasHabilitadas.length > 0);
      if (!isAdmin && esPersonalizado) {
        const userCasaId = userData?.casaId || userDebt?.casaData?.id;
        const userNumeroCasa = String(userData?.numeroCasa || userDebt?.casaData?.numero || '').trim().toLowerCase();
        const habilitadas = Array.isArray(notif.casasHabilitadas) ? notif.casasHabilitadas.map(String) : [];
        const numsHabilitados = Array.isArray(notif.numerosCasasHabilitadas) ? notif.numerosCasasHabilitadas.map(n => String(n).trim().toLowerCase()) : [];
        const matchCasaId = userCasaId && habilitadas.includes(String(userCasaId));
        const matchNum = userNumeroCasa && (numsHabilitados.includes(userNumeroCasa) || habilitadas.includes(userNumeroCasa));
        if (!matchCasaId && !matchNum) return false;
      }

      // Filtro del mes actual
      let d = null;
      if (notif.fecha?.toDate && typeof notif.fecha.toDate === 'function') {
        d = notif.fecha.toDate();
      } else if (notif.fecha instanceof Date) {
        d = notif.fecha;
      } else if (notif.fecha?.seconds) {
        d = new Date(notif.fecha.seconds * 1000);
      } else if (typeof notif.fecha === 'string' || typeof notif.fecha === 'number') {
        d = new Date(notif.fecha);
      }

      if (d && !isNaN(d.getTime())) {
        if (d.getMonth() !== mesActual || d.getFullYear() !== anioActual) {
          return false; // Almacenada en Firestore, omitida de la campana activa
        }
      }

      return true;
    });
  }, [notifications, isAdmin, isResidentMoroso]);

  // Actualizar conteo de no leídas basado en notificaciones activas del mes
  useEffect(() => {
    const unread = notificacionesMesActual.filter(n => !n.leida).length;
    setUnreadCount(unread);
  }, [notificacionesMesActual]);

  // Calcular contadores por sección
  const sectionCounts = useMemo(() => {
    const counts = {
      todas: { total: notificacionesMesActual.length, unread: 0 },
      comunidad: { total: 0, unread: 0 },
      amenidades: { total: 0, unread: 0 },
      vigilancia: { total: 0, unread: 0 },
      finanzas: { total: 0, unread: 0 },
      administracion: { total: 0, unread: 0 },
      configuraciones: { total: 0, unread: 0 },
    };

    notificacionesMesActual.forEach(notif => {
      const section = getNotificationSection(notif.tipo);
      if (counts[section]) {
        counts[section].total++;
        if (!notif.leida) {
          counts[section].unread++;
          counts.todas.unread++;
        }
      }
    });

    return counts;
  }, [notificacionesMesActual]);

  // Filtrar notificaciones según la pestaña seleccionada
  const filteredNotifications = useMemo(() => {
    if (selectedTab === 'todas') {
      return notificacionesMesActual;
    }
    return notificacionesMesActual.filter(notif => getNotificationSection(notif.tipo) === selectedTab);
  }, [notificacionesMesActual, selectedTab]);

  // No mostrar NotificationBell para rol platformadmin o caseta
  const rol = userData?.rol?.toLowerCase().trim() || '';
  if (rol === 'platformadmin' || rol === 'caseta') {
    return null;
  }

  const handleMarkAsRead = async (notif) => {
    try {
      // Si es un objeto, usar directamente; si es solo el ID, buscar la notificación
      const notifData = typeof notif === 'object' ? notif : notifications.find(n => n.id === notif);
      const notifId = typeof notif === 'object' ? notif.id : notif;

      if (!notifData) {
        console.warn('handleMarkAsRead: Notificación no encontrada', notifId);
        return;
      }

      // Marcar como leída en la colección personal del usuario
      const userNotifRef = doc(db, 'usuarios', currentUser.uid, 'notificaciones', notifId);
      await updateDoc(userNotifRef, { leida: true });

      // Si es una incidencia o propuesta, también marcar el reporte original como leído
      if ((notifData.tipo === 'incidencia' || notifData.tipo === 'propuesta') && notifData.reportId && notifData.fraccionamientoId) {
        try {
          const lecturaRef = doc(
            db,
            'fraccionamientos',
            notifData.fraccionamientoId,
            'propuestasIncidencias',
            notifData.reportId,
            'lecturas',
            currentUser.uid
          );
          await setDoc(lecturaRef, {
            leidoEn: serverTimestamp(),
            usuarioId: currentUser.uid,
            usuarioNombre: userData?.nombre || userData?.nombreCompleto || currentUser?.displayName || null,
            usuarioEmail: userData?.email || currentUser?.email || null,
          }, { merge: true });
          console.log('✅ Reporte de incidencia/propuesta marcado como leído desde la campana');
        } catch (reportErr) {
          console.warn('Error marcando reporte como leído desde la campana:', reportErr);
        }
      }

      // Si es una notificación de pago, también marcarla como leída en la colección del fraccionamiento
      if (notifData.tipo === 'pago_registrado' && userData?.fraccionamientoId) {
        try {
          const fraccNotifRef = collection(db, 'fraccionamientos', userData.fraccionamientoId, 'notificaciones');
          let fraccNotifSnap = null;

          // Intentar buscar por ingresoId si existe
          if (notifData.ingresoId) {
            const fraccNotifQuery = query(
              fraccNotifRef,
              where('ingresoId', '==', notifData.ingresoId),
              where('destinatarioUid', '==', currentUser.uid),
              where('tipoMovimiento', '==', 'pago_registrado')
            );
            fraccNotifSnap = await getDocs(fraccNotifQuery);
          }

          // Si no se encontró por ingresoId, buscar por monto y fecha (último recurso)
          if (!fraccNotifSnap || fraccNotifSnap.empty) {
            const fraccNotifQuery = query(
              fraccNotifRef,
              where('destinatarioUid', '==', currentUser.uid),
              where('tipoMovimiento', '==', 'pago_registrado'),
              where('monto', '==', notifData.monto || 0),
              where('leida', '==', false)
            );
            fraccNotifSnap = await getDocs(fraccNotifQuery);

            // Filtrar por fecha si es posible (comparar timestamps)
            if (!fraccNotifSnap.empty && notifData.fecha) {
              const notifFecha = notifData.fecha?.toDate ? notifData.fecha.toDate() : new Date(notifData.fecha);
              const matchingDocs = fraccNotifSnap.docs.filter(doc => {
                const docFecha = doc.data().fecha?.toDate ? doc.data().fecha.toDate() : new Date(doc.data().fecha || 0);
                // Comparar fechas con tolerancia de 1 minuto
                return Math.abs(docFecha.getTime() - notifFecha.getTime()) < 60000;
              });

              if (matchingDocs.length > 0) {
                fraccNotifSnap = { docs: matchingDocs, empty: false };
              }
            }
          }

          if (fraccNotifSnap && !fraccNotifSnap.empty) {
            const batch = writeBatch(db);
            fraccNotifSnap.docs.forEach(doc => {
              batch.update(doc.ref, { leida: true });
            });
            await batch.commit();
            console.log('✅ Notificación de pago marcada como leída en fraccionamiento', {
              encontradas: fraccNotifSnap.docs.length,
              ingresoId: notifData.ingresoId
            });
          } else {
            console.warn('⚠️ No se encontró notificación en fraccionamiento para marcar como leída', {
              ingresoId: notifData.ingresoId,
              monto: notifData.monto
            });
          }
        } catch (fraccErr) {
          console.warn('Error marcando notificación en fraccionamiento:', fraccErr);
        }
      }
    } catch (err) {
      console.error('Error marcando notificación como leída', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.leida);
      const batch = writeBatch(db);
      const fraccBatch = writeBatch(db);
      let fraccBatchCount = 0;

      for (const notif of unreadNotifications) {
        // Marcar como leída en la colección personal del usuario
        const notifRef = doc(db, 'usuarios', currentUser.uid, 'notificaciones', notif.id);
        batch.update(notifRef, { leida: true });

        // Si es una notificación de pago, también marcarla en el fraccionamiento
        if (notif.tipo === 'pago_registrado' && userData?.fraccionamientoId && notif.ingresoId) {
          try {
            const fraccNotifQuery = query(
              collection(db, 'fraccionamientos', userData.fraccionamientoId, 'notificaciones'),
              where('ingresoId', '==', notif.ingresoId),
              where('destinatarioUid', '==', currentUser.uid),
              where('tipoMovimiento', '==', 'pago_registrado')
            );
            const fraccNotifSnap = await getDocs(fraccNotifQuery);
            if (!fraccNotifSnap.empty) {
              fraccNotifSnap.docs.forEach(doc => {
                fraccBatch.update(doc.ref, { leida: true });
                fraccBatchCount++;
              });
            }
          } catch (fraccErr) {
            console.warn('Error buscando notificación en fraccionamiento:', fraccErr);
          }
        }

        // Si es una incidencia o propuesta, también marcar el reporte original como leído
        if ((notif.tipo === 'incidencia' || notif.tipo === 'propuesta') && notif.reportId && notif.fraccionamientoId) {
          try {
            const lecturaRef = doc(
              db,
              'fraccionamientos',
              notif.fraccionamientoId,
              'propuestasIncidencias',
              notif.reportId,
              'lecturas',
              currentUser.uid
            );
            await setDoc(lecturaRef, {
              leidoEn: serverTimestamp(),
              usuarioId: currentUser.uid,
              usuarioNombre: userData?.nombre || userData?.nombreCompleto || currentUser?.displayName || null,
              usuarioEmail: userData?.email || currentUser?.email || null,
            }, { merge: true });
          } catch (reportErr) {
            console.warn('Error marcando reporte como leído masivamente:', reportErr);
          }
        }
      }

      // Ejecutar ambos batches
      await batch.commit();
      if (fraccBatchCount > 0) {
        await fraccBatch.commit();
        console.log(`✅ ${fraccBatchCount} notificaciones de pago marcadas como leídas en fraccionamiento`);
      }
    } catch (err) {
      console.error('Error marcando todas como leídas', err);
    }
  };

  const handleNavigateToSection = async (notif) => {
    // Si es una notificación de pago, marcarla como leída en ambas colecciones
    if (notif.tipo === 'pago_registrado' && userData?.fraccionamientoId && !notif.leida) {
      try {
        // Marcar como leída en la colección personal del usuario
        const userNotifRef = doc(db, 'usuarios', currentUser.uid, 'notificaciones', notif.id);
        await updateDoc(userNotifRef, { leida: true });

        // También marcar como leída en la colección global del fraccionamiento si existe
        if (notif.ingresoId) {
          try {
            const fraccNotifQuery = query(
              collection(db, 'fraccionamientos', userData.fraccionamientoId, 'notificaciones'),
              where('ingresoId', '==', notif.ingresoId),
              where('destinatarioUid', '==', currentUser.uid),
              where('tipoMovimiento', '==', 'pago_registrado')
            );
            const fraccNotifSnap = await getDocs(fraccNotifQuery);
            if (!fraccNotifSnap.empty) {
              const batch = writeBatch(db);
              fraccNotifSnap.docs.forEach(doc => {
                batch.update(doc.ref, { leida: true });
              });
              await batch.commit();
            }
          } catch (fraccErr) {
            console.warn('Error marcando notificación en fraccionamiento:', fraccErr);
          }
        }
      } catch (err) {
        console.error('Error marcando notificación como leída:', err);
      }
    }

    switch (notif.tipo) {
      // Comunidad — votaciones
      case 'votacion':
        navigate('/comunidad/votaciones-avisos');
        break;
      // Comunidad — avisos
      case 'aviso':
      case 'aviso_general':
        navigate('/comunidad/votaciones-avisos#avisos');
        break;
      // Comunidad — eventos
      case 'evento_cita':
        navigate('/comunidad/eventos');
        break;
      // Comunidad — chat
      case 'chat_mensaje':
        navigate('/comunidad/chat');
        break;
      // Comunidad — reglamentos
      case 'reglamento_nuevo':
        navigate('/comunidad/documentos?tab=reglamentos');
        break;
      // Comunidad — propuestas/incidencias
      case 'propuesta':
      case 'incidencia':
        navigate('/comunidad/propuestas');
        break;
      // Comunidad — solicitudes
      case 'solicitud_mudanza':
      case 'solicitud_construccion':
      case 'solicitud_remodelacion':
      case 'solicitud':
        navigate('/comunidad/documentos?tab=solicitudes');
        break;
      // Comunidad — documentos/permisos
      case 'documento':
      case 'permiso':
        if (notif.casaId) {
          navigate('/comunidad/documentos?tab=mi-documentacion');
        } else {
          navigate('/comunidad/documentos?tab=reglamentos');
        }
        break;
      // Amenidades
      case 'reservacion':
      case 'reservacion_confirmada':
      case 'reservacion_cancelada':
      case 'reserva_amenidad':
      case 'reserva_cancelada':
        navigate('/amenidades');
        break;
      // Vigilancia — visitas
      case 'visita_frecuente':
      case 'visita_frecuente_actualizada':
      case 'visita_frecuente_eliminada':
      case 'visita_frecuente_nueva':
      case 'acceso_autorizado':
      case 'acceso_qr_validado':
      case 'qr_generado_vigilancia':
      case 'qr_visita_entrada':
      case 'qr_visita_salida':
      case 'entrada_manual':
      case 'visita':
      case 'visita_registrada':
        navigate('/vigilancia');
        break;
      // Vigilancia — paquetería
      case 'paquete':
      case 'paquete_nuevo':
      case 'paquete_entregado':
        navigate('/vigilancia');
        break;
      // Finanzas
      case 'comprobante_pago':
      case 'comprobante':
        if (notif.casa) {
          window.dispatchEvent(new CustomEvent('domus_abrir_modal_pago', { detail: { casaNumero: notif.casa } }));
        }
        if (notif.archivoUrl) {
          window.open(notif.archivoUrl, '_blank');
        } else {
          navigate('/finanzas');
        }
        break;
      case 'finanzas':
      case 'pago_registrado':
      case 'advertencia_corte':
      case 'corte_aplicado':
      case 'sanciones_aplicadas':
      case 'multa_admin':
      case 'multa_leida_confirmacion':
      case 'cambio_configuracion_financiera':
        navigate('/finanzas');
        break;
      // Administración
      case 'correo_personalizado':
      case 'convenio_activado':
      case 'convenio_confirmado':
        navigate('/administracion');
        break;
      // Configuraciones
      case 'horario_mudanza':
      case 'horario_amenidad':
      case 'reglamento_circulacion':
      case 'configuracion_general':
      case 'configuracion':
      case 'cambio_configuracion':
        navigate('/configuraciones');
        break;
      default:
        break;
    }
    setShowPanel(false);
  };

  const getNotificationIcon = (tipo) => {
    const iconMap = {
      // Finanzas
      'pago_registrado': <FaDollarSign />,
      'advertencia_corte': <FaExclamationTriangle />,
      'corte_aplicado': <FaExclamationTriangle />,
      'sanciones_aplicadas': <FaExclamationTriangle />,
      'cambio_configuracion_financiera': <FaFileAlt />,
      'multa_admin': <FaExclamationTriangle />,
      'multa_leida_confirmacion': <FaCheckCircle />,
      // Votaciones y Avisos
      'votacion': <FaFileAlt />,
      'aviso': <FaFileAlt />,
      'aviso_general': <FaFileAlt />,
      // Eventos y Chat
      'evento_cita': <FaCheckCircle />,
      'chat_mensaje': <FaBell />,
      'reglamento_nuevo': <FaFileAlt />,
      // Propuestas e Incidencias
      'propuesta': <FaFileAlt />,
      'incidencia': <FaExclamationTriangle />,
      // Solicitudes
      'solicitud_mudanza': <FaFileAlt />,
      'solicitud_construccion': <FaFileAlt />,
      'solicitud_remodelacion': <FaFileAlt />,
      'solicitud': <FaFileAlt />,
      // Documentos y Permisos
      'documento': <FaFileAlt />,
      'permiso': <FaCheckCircle />,
      // Amenidades
      'reserva_amenidad': <FaCheckCircle />,
      'reserva_cancelada': <FaExclamationTriangle />,
      // Vigilancia
      'visita_frecuente': <FaUserPlus />,
      'visita_frecuente_actualizada': <FaFileAlt />,
      'visita_frecuente_eliminada': <FaFileAlt />,
      'visita_frecuente_nueva': <FaUserPlus />,
      'acceso_autorizado': <FaCheckCircle />,
      'acceso_qr_validado': <FaCheckCircle />,
      'qr_generado_vigilancia': <FaFileAlt />,
      'qr_visita_entrada': <FaCheckCircle />,
      'qr_visita_salida': <FaBell />,
      'paquete': <FaFileAlt />,
      'paquete_nuevo': <FaBell />,
      'paquete_entregado': <FaCheckCircle />,
      'entrada_manual': <FaBell />,
      'visita': <FaUserPlus />,
      'visita_registrada': <FaCheckCircle />,
      // Administración
      'correo_personalizado': <FaBell />,
      'convenio_activado': <FaCheckCircle />,
      'convenio_confirmado': <FaCheckCircle />,
      // Configuraciones
      'horario_mudanza': <FaFileAlt />,
      'horario_amenidad': <FaFileAlt />,
      'reglamento_circulacion': <FaFileAlt />,
      'configuracion_general': <FaFileAlt />,
      // Default
      'default': <FaBell />
    };
    return iconMap[tipo] || iconMap['default'];
  };

  const getNotificationIconColor = (tipo) => {
    const colorMap = {
      // Finanzas
      'pago_registrado': '#10b981',
      'advertencia_corte': '#ef4444',
      'corte_aplicado': '#ef4444',
      'sanciones_aplicadas': '#ef4444',
      'cambio_configuracion_financiera': '#3b82f6',
      'multa_admin': '#ef4444',
      'multa_leida_confirmacion': '#10b981',
      // Votaciones y Avisos
      'votacion': '#3b82f6',
      'aviso': '#3b82f6',
      'aviso_general': '#3b82f6',
      // Eventos y Chat
      'evento_cita': '#6366f1',
      'chat_mensaje': '#8b5cf6',
      'reglamento_nuevo': '#0ea5e9',
      // Propuestas e Incidencias
      'propuesta': '#3b82f6',
      'incidencia': '#ef4444',
      // Solicitudes
      'solicitud_mudanza': '#f97316',
      'solicitud_construccion': '#f97316',
      'solicitud_remodelacion': '#f97316',
      'solicitud': '#3b82f6',
      // Documentos y Permisos
      'documento': '#3b82f6',
      'permiso': '#10b981',
      // Amenidades
      'reserva_amenidad': '#6366f1',
      'reserva_cancelada': '#ef4444',
      // Vigilancia
      'visita_frecuente': '#3b82f6',
      'visita_frecuente_actualizada': '#3b82f6',
      'visita_frecuente_eliminada': '#ef4444',
      'visita_frecuente_nueva': '#3b82f6',
      'acceso_autorizado': '#10b981',
      'acceso_qr_validado': '#10b981',
      'qr_generado_vigilancia': '#3b82f6',
      'qr_visita_entrada': '#10b981',
      'qr_visita_salida': '#64748b',
      'paquete': '#f97316',
      'paquete_nuevo': '#f97316',
      'paquete_entregado': '#10b981',
      'entrada_manual': '#64748b',
      'visita': '#3b82f6',
      'visita_registrada': '#10b981',
      // Administración
      'correo_personalizado': '#8b5cf6',
      'convenio_activado': '#0ea5e9',
      'convenio_confirmado': '#10b981',
      // Configuraciones
      'horario_mudanza': '#64748b',
      'horario_amenidad': '#64748b',
      'reglamento_circulacion': '#64748b',
      'configuracion_general': '#64748b',
      // Default
      'default': '#64748b'
    };
    return colorMap[tipo] || colorMap['default'];
  };

  const getTimeAgo = (fecha) => {
    if (!fecha) return 'Ahora';

    let date;
    if (fecha.toDate) {
      date = fecha.toDate();
    } else if (fecha instanceof Date) {
      date = fecha;
    } else {
      return 'Ahora';
    }

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Hace un momento';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours} h`;
    if (diffDays < 7) return `Hace ${diffDays} d`;

    return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  };

  if (loading || !currentUser) return null;

  return (
    <div className="notification-bell-container">
      <button
        ref={buttonRef}
        className={`notification-bell-button ${unreadCount > 0 ? 'has-unread' : ''}`}
        onClick={() => setShowPanel(!showPanel)}
        title="Notificaciones"
      >
        <FaBell className={`bell-icon ${unreadCount > 0 ? 'shake' : ''}`} />
        <span className="notification-bell-text">Notificaciones</span>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {showPanel && (
        <div ref={panelRef} className="notification-panel">
          <div className="notification-panel-header">
            <h3>Notificaciones</h3>
            <button
              className="close-btn"
              onClick={() => setShowPanel(false)}
              title="Cerrar panel"
            >
              <FaTimes />
            </button>
          </div>

          {/* Tabs de secciones con indicador visual de desplazamiento */}
          <div className="notification-tabs-container">
            <div className="notification-tabs">
              <button
                className={`notification-tab ${selectedTab === 'todas' ? 'active' : ''}`}
                onClick={() => setSelectedTab('todas')}
              >
                Todas
                {sectionCounts.todas.unread > 0 && (
                  <span className="notification-tab-badge">{sectionCounts.todas.unread}</span>
                )}
              </button>
              <button
                className={`notification-tab ${selectedTab === 'comunidad' ? 'active' : ''}`}
                onClick={() => setSelectedTab('comunidad')}
              >
                <FaUsers />
                Comunidad
                {sectionCounts.comunidad.unread > 0 && (
                  <span className="notification-tab-badge">{sectionCounts.comunidad.unread}</span>
                )}
              </button>
              <button
                className={`notification-tab ${selectedTab === 'amenidades' ? 'active' : ''}`}
                onClick={() => setSelectedTab('amenidades')}
              >
                <FaSwimmingPool />
                Amenidades
                {sectionCounts.amenidades.unread > 0 && (
                  <span className="notification-tab-badge">{sectionCounts.amenidades.unread}</span>
                )}
              </button>
              <button
                className={`notification-tab ${selectedTab === 'vigilancia' ? 'active' : ''}`}
                onClick={() => setSelectedTab('vigilancia')}
              >
                <FaShieldAlt />
                Vigilancia
                {sectionCounts.vigilancia.unread > 0 && (
                  <span className="notification-tab-badge">{sectionCounts.vigilancia.unread}</span>
                )}
              </button>
              <button
                className={`notification-tab ${selectedTab === 'finanzas' ? 'active' : ''}`}
                onClick={() => setSelectedTab('finanzas')}
              >
                <FaDollarSign />
                Finanzas
                {sectionCounts.finanzas.unread > 0 && (
                  <span className="notification-tab-badge">{sectionCounts.finanzas.unread}</span>
                )}
              </button>
              <button
                className={`notification-tab ${selectedTab === 'configuraciones' ? 'active' : ''}`}
                onClick={() => setSelectedTab('configuraciones')}
              >
                <FaCogs />
                Config
                {sectionCounts.configuraciones.unread > 0 && (
                  <span className="notification-tab-badge">{sectionCounts.configuraciones.unread}</span>
                )}
              </button>
            </div>
          </div>

          <div className="notification-panel-actions">
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
              >
                Marcar todas como leídas
              </button>
            )}
          </div>

          <div ref={listRef} className="notification-list">
            {filteredNotifications.length === 0 ? (
              <div className="no-notifications">
                <FaBell className="no-notifications-icon" />
                <p>No tienes notificaciones{selectedTab !== 'todas' ? ` en ${selectedTab}` : ''}</p>
              </div>
            ) : (
              filteredNotifications.map(notif => {
                const iconColor = getNotificationIconColor(notif.tipo);
                return (
                  <div
                    key={notif.id}
                    className={`notification-item ${notif.leida ? 'read' : 'unread'}`}
                    onClick={() => handleNavigateToSection(notif)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="notification-icon-wrapper" style={{ color: iconColor }}>
                      {getNotificationIcon(notif.tipo)}
                    </div>
                    <div className="notification-content">
                      <div className="notification-title-row">
                        <span className="notification-title">{limpiarTextoNotificacion(notif.titulo)}</span>
                        {!notif.leida && <span className="unread-indicator"></span>}
                      </div>
                      <p className="notification-description">{limpiarTextoNotificacion(notif.descripcion)}</p>
                      <span className="notification-time">
                        {getTimeAgo(notif.fecha)}
                      </span>
                    </div>
                    <div className="notification-actions">
                      {!notif.leida && (
                        <button
                          className="notification-mark-read-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notif);
                          }}
                          title="Marcar como leída"
                        >
                          <FaCheckCircle />
                        </button>
                      )}
                      <button
                        className="notification-navigate-btn"
                        onClick={() => handleNavigateToSection(notif)}
                        title="Ir a la sección"
                      >
                        <FaArrowRight />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default NotificationBell;
